import { FC, useEffect, useState } from 'react'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import { Theme, ThemeTranslation } from '../core/_models'
import clsx from 'clsx'
import { useListView } from '../core/ListViewProvider'
import { DataLoading } from '../../../../../_dashboard/partials/components/loading/DataLoading'
import { useQueryResponse } from '../core/QueryResponseProvider'
import { createOne, getAll, getOneByIdbylang, updateOne } from '../../../../services/common_requests'
import { useIntl } from 'react-intl'
import { useQueryRequest } from '../core/QueryRequestProvider'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { isNotEmpty } from '../../../../../_dashboard/helpers'
type Props = {
  isThemeLoading: boolean
  Theme: Theme,
}

const editThemeSchema = Yup.object().shape({
  translations: Yup.array().of(
    Yup.object().shape({
      name: Yup.string()
        .min(3, 'Minimum 3 symbols')
        .max(50, 'Maximum 50 symbols')
        .required('Title is required'),
    })
  ),
});

const ThemeEditModalForm: FC<Props> = ({ Theme, isThemeLoading }) => {
  const LANGUAGE_NAMES: { [key: string]: string } = {
    en: useIntl().formatMessage({ id: 'COMMON.ENGLISH_LANGUAGE' }),
    fr: useIntl().formatMessage({ id: 'COMMON.FRENSH_LANGUAGE' }),
    es: useIntl().formatMessage({ id: 'COMMON.ESPANGOL_LANGUAGE' }),
    ar: useIntl().formatMessage({ id: 'COMMON.ARABIC_LANGUAGE' }),
  };
  const { itemIdForUpdate, setItemIdForUpdate } = useListView()
  const { state } = useQueryRequest();
  const [query, setQuery] = useState<any>(state);
  const [languages, setLanguages] = useState<{ code: string; name: string }[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  useEffect(() => {
    getAll(query, '/languages').then((data) => {
      if (data && Array.isArray(data)) {
        const formattedLanguages = data.map((code: string) => ({
          code,
          name: LANGUAGE_NAMES[code] || code.toUpperCase(),
        }));
        setLanguages(formattedLanguages);
      }
    });
  }, [query]);

  useEffect(() => {
    if (itemIdForUpdate && selectedLanguage) {
      getOneByIdbylang(`/themes/${itemIdForUpdate}?language=${selectedLanguage}`)
        .then((data) => {
          if (data && data.translations) {
            // Mettre à jour toutes les traductions
            formik.setValues({
              ...formik.values,
              translations: data.translations.map((t: any) => ({
                ...t,
                _id: t._id 
              }))
            });
          }
        })
        .catch((error) => console.error('Error fetching translation:', error));
    }
  }, [selectedLanguage, itemIdForUpdate]);

  const { refetchData } = useQueryResponse()
  const intl = useIntl()
  const [ThemeForEdit] = useState<Theme & { translations: ThemeTranslation[] }>({
    ...Theme,
    translations: Theme.translations ?? [{ language: 'en', name: '' }]
    
  })

  const cancel = (withRefresh?: boolean) => {
    if (withRefresh) {
      refetchData()
    }
    setItemIdForUpdate(undefined)
  }
  const formik = useFormik({
    initialValues: ThemeForEdit,
    validationSchema: editThemeSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      if (isNotEmpty(itemIdForUpdate)) {
        const selectedTranslation = values.translations.find(t => t.language === selectedLanguage);

        if (!selectedTranslation || !selectedTranslation._id) {
          toast.error(intl.formatMessage({ id: 'ADMIN.COMMON.ERROR.ID_NOT_FOUND' }));
          return;
        }
        const payload = {
          name: selectedTranslation.name,
        };

        await updateOne(payload, `/themes/translation/${selectedTranslation._id}`).then((response) => {
          if (response?.payload?.code === 2021) {
            toast.success(
              intl.formatMessage({
                id: 'ADMIN.COMMON.SUCCESS.OPTION_UPDATED_SUCCESSFULLY',
              }),
              {
                position: 'top-right',
                autoClose: 4000,
              }
            );
            cancel(true)
            refetchData()
          }
          else if (response?.payload?.code === 4401) {
            toast.error(
              intl.formatMessage({
                id: 'ADMIN.COMMON.ERROR.ID_NOT_EXIST',
              }),
              {
                position: 'top-right',
                autoClose: 4000,
              }
            );
          }
          else if (response?.payload?.code === 9100) {
            toast.error(
              intl.formatMessage({
                id: 'ADMIN.COMMON.ERROR.ID_NOT_FOUND',
              }),
              {
                position: 'top-right',
                autoClose: 4000,
              }
            );
          }
          else {
            toast.error(
              intl.formatMessage({
                id: 'ADMIN.COMMON.ERROR.ERROR_ADDING_OPTION',
              }),
              {
                position: 'top-right',
                autoClose: 4000,
              }
            );
          }
        })

      }
      else {
        const payload = {
          name: values.translations[0].name,
          translations: values.translations.map(translation => ({
            language: translation.language || 'en',
            name: translation.name,
          })),
        };
        try {

          await createOne(payload, '/themes').then((response) => {
            if (response?.payload?.code === 2021) {

              toast.success(
                intl.formatMessage({
                  id: 'ADMIN.COMMON.SUCCESS.THEMES_ADDED_SUCCESSFULLY',
                }),
                {
                  position: 'top-right',
                  autoClose: 4000,
                }
              );
              cancel(true)
            }
            else if (response?.payload?.code === 4400) {
              toast.error(
                intl.formatMessage({
                  id: 'ADMIN.COMMON.ERROR.NAME_ALREADY_EXIST',
                }),
                {
                  position: 'top-right',
                  autoClose: 4000,
                }
              );
            }
            else {
              toast.error(
                intl.formatMessage({
                  id: 'ADMIN.COMMON.ERROR.ERROR_ADDING_THEME',
                }),
                {
                  position: 'top-right',
                  autoClose: 4000,
                }
              );
            }
          })
        }
        catch (ex) {
          console.error(ex)
        } finally {
          setSubmitting(true)
          cancel(true)
        }
      }
    },
  })
  const selectedTranslation = formik.values.translations.find(t => t.language === selectedLanguage);

  return (
    <>
      <form
        id='kt_modal_add_theme_form'
        className='form'
        onSubmit={formik.handleSubmit}
        noValidate
      >
        {/* begin::Scroll */}
        <div
          className='d-flex flex-column scroll-y me-n15 pe-6'
          id='kt_modal_add_theme_scroll'
          data-kt-scroll='true'
          data-kt-scroll-activate='{default: false, lg: true}'
          data-kt-scroll-max-height='auto'
          data-kt-scroll-dependencies='#kt_modal_add_theme_header'
          data-kt-scroll-wrappers='#kt_modal_add_theme_scroll'
          data-kt-scroll-offset='300px'
        >
          {/* begin::Input group */}
          <div className='fv-row mb-4'>
            <label className='required fw-bold fs-6 mb-2'>
              {intl.formatMessage({ id: 'COLUMNS.LANGUAGE' })}
            </label>
            {formik.values.translations.map((translation, index) => (
              <div key={index}>
                <select
                  id={`translations[${index}].language`}
                  name={`translations[${index}].language`}
                  onChange={(e) => {
                    formik.handleChange(e);
                    setSelectedLanguage(e.target.value); // Met à jour la langue sélectionnée
                  }}
                  value={translation.language}

                  className='form-select'
                >
                  {languages.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name}
                    </option>
                  ))}
                </select>
                {formik.errors.translations && (formik.errors.translations[index] as { language?: string })?.language && (
                  <div>{(formik.errors.translations[index] as { language: string }).language}</div>
                )}
              </div>
            ))}
          </div>
          {/* end::Input group */}

          {/* begin::Input group */}
          <div className='fv-row mb-4'>
            {/* begin::Label */}
            <label className='required fw-bold fs-6 mb-2'>
              {intl.formatMessage({ id: 'COLUMNS.NAME' })}
            </label>
            {/* end::Label */}

            {/* begin::Input */}
            <input
              placeholder={intl.formatMessage({ id: 'COLUMNS.NAME' })}
              value={selectedTranslation?.name || ''}
              onChange={(e) => {
                const index = formik.values.translations.findIndex(t => t.language === selectedLanguage);
                if (index !== -1) {
                  formik.setFieldValue(`translations[${index}].name`, e.target.value);
                }
              }}
              type="text"
              name="name"
              className={clsx(
                'form-control mb-3 mb-lg-0',
                { 'is-invalid': formik.touched.translations?.[0]?.name && (formik.errors.translations?.[0] as { name?: string })?.name },
                { 'is-valid': formik.touched.translations?.[0]?.name && !(formik.errors.translations?.[0] as { name?: string })?.name }
              )}
              autoComplete="off"
              disabled={formik.isSubmitting || isThemeLoading}
            />

            {/* end::Input */}
          </div>
          {/* end::Input group */}
        </div>
        {/* end::Scroll */}

        {/* begin::Actions */}
        <div className='text-center pt-15'>
          <button
            type='reset'
            onClick={() => cancel()}
            className='btn btn-light me-3'
            data-kt-themes-modal-action='cancel'
            disabled={formik.isSubmitting || isThemeLoading}
          >
            {intl.formatMessage({ id: 'HEADER.RESET' })}
          </button>

          <button
            type='submit'
            className='btn btn-primary'
            data-kt-users-modal-action='submit'
            disabled={
              isThemeLoading || formik.isSubmitting || !formik.isValid || !formik.touched
            }
          >
            <span className='indicator-label'> {intl.formatMessage({ id: 'HEADER.SAVE' })}
            </span>
            {(formik.isSubmitting || isThemeLoading) && (
              <span className='indicator-progress'>
                {intl.formatMessage({ id: 'COMMON.PLEASE_WAIT' })}{' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
        {/* end::Actions */}
      </form>
      {(formik.isSubmitting || isThemeLoading) && <DataLoading />}
    </>
  )
}

export { ThemeEditModalForm }
